import apiClient from './apiClient';

/**
 * How many scripts the patient chart pulls in one request. High enough
 * that no realistic single patient is truncated, low enough that a bad
 * id can't stream the clinic's whole prescription table into the browser.
 */
export const CHART_RX_PAGE = 300;

// Backend expected structure
export interface CreatePrescriptionDto {
  prescriptionNumber: string;
  status?: string;
  priority?: string;
  // Optional FK to the tenant medications formulary. When set, the
  // backend validates the row exists, is active, and not recalled.
  medicationId?: string;
  // Optional encounter / shared-batch anchors for session grouping. The
  // batch endpoint stamps prescriptionGroupId server-side, so callers
  // rarely set it directly.
  medicalRecordId?: string;
  prescriptionGroupId?: string;
  patientId: string;
  patientName: string;
  patientDOB: string;
  patientAllergies?: string[];
  medication: {
    name: string;
    genericName: string;
    brandName?: string;
    strength: string;
    form: string;
    route: string;
    ndc?: string;
    rxcui?: string;
    isControlledSubstance: boolean;
  };
  dosageInstructions: {
    dose: string;
    route: string;
    frequency: string;
    duration?: string;
    instructions?: string;
  };
  quantityToDispense: number;
  quantityUnit: string;
  daysSupply: number;
  refills: {
    authorized: number;
    remaining: number;
    status?: string;
  };
  dispenseAsWritten?: string;
  substitutionAllowed?: boolean;
  pharmacy: {
    name: string;
    ncpdpId?: string;
    npi?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    phone?: string;
  };
  pharmacyNotes?: {
    en?: string;
    ar?: string;
  };
  prescriberId: string;
  prescriberInfo: {
    name: string;
    npi?: string;
    deaNumber?: string;
    stateLicense?: string;
    phone?: string;
    address?: string;
  };
  diagnosis: Array<{
    code?: string;
    description?: string;
  }>;
  indication: {
    en?: string;
    ar?: string;
    condition?: string;
    notes?: string;
  };
  clinicalNotes?: {
    en?: string;
    ar?: string;
  };
  writtenDate: string;
  expirationDate: string;
}

export type UpdatePrescriptionDto = Partial<CreatePrescriptionDto>;

export interface PrescriptionFilterDto {
  patientId?: string;
  prescriberId?: string;
  status?: string;
  priority?: string;
  isControlledSubstance?: boolean;
  search?: string;
  page?: number;
  limit?: number;
  sort?: 'writtenDate' | 'createdAt';
  order?: 'ASC' | 'DESC';
}

export interface PaginatedPrescriptions {
  data: Prescription[];
  total: number;
  page: number;
  limit: number;
}

// A "session" group — every medication written out of the same encounter
// treated as one unit (standalone scripts form a singleton group keyed by
// their own id). List views label it by patient + session date; the detail
// shows all `items` prescribed together.
export interface PrescriptionGroup {
  groupKey: string;
  medicalRecordId: string | null;
  patientId: string;
  patientName?: string;
  writtenDate?: Date;
  status: string;
  itemCount: number;
  items: Prescription[];
}

export interface PaginatedPrescriptionGroups {
  data: PrescriptionGroup[];
  total: number;
  page: number;
  limit: number;
}

export interface Prescription {
  id: string;
  patientId: string;
  // Denormalized + display-only (encrypted at rest, so not searchable
  // server-side). Present on clinic-wide list reads.
  patientName?: string;
  medicalRecordId?: string | null;
  prescriptionGroupId?: string | null;
  medicationId?: string | null;
  medicationName: string;
  medicationGenericName?: string;
  medicationBrandName?: string;
  strength?: string;
  dosageForm?: string;
  ndcCode?: string;
  deaSchedule?: string;
  isControlledSubstance: boolean;
  dose?: string;
  doseUnit?: string;
  route?: string;
  frequency?: string;
  duration?: number;
  durationUnit?: string;
  timing?: string;
  maxDailyDose?: string;
  additionalInstructions?: string;
  quantity?: number;
  quantityUnit?: string;
  daysSupply?: number;
  refillsAuthorized: number;
  refillsRemaining: number;
  refillStatus?: string;
  allowSubstitution: boolean;
  dawCode?: string;
  pharmacyId?: string;
  pharmacyName?: string;
  pharmacyNcpdpId?: string;
  pharmacyNpi?: string;
  pharmacyPhone?: string;
  pharmacyAddress?: string;
  pharmacyCity?: string;
  pharmacyState?: string;
  pharmacyZip?: string;
  prescriberId?: string;
  prescriberNpi?: string;
  prescriberDeaNumber?: string;
  prescriberLicenseNumber?: string;
  prescriberPhone?: string;
  diagnosis?: string;
  diagnosisCode?: string;
  indication?: string;
  clinicalNotes?: string;
  priority: string;
  status: string;
  writtenDate?: Date;
  effectiveDate?: Date;
  expirationDate?: Date;
  discontinuedDate?: Date;
  signedDate?: Date;
  signatureHash?: string;
  signedByUserId?: string;
  signatureIpAddress?: string;
  transmittedDate?: Date;
  transmissionId?: string;
  transmissionConfirmation?: string;
  pharmacyReceivedDate?: Date;
  dispensedDate?: Date;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  modifiedBy?: string;
  cancelledBy?: string;
  cancelledAt?: Date;
  cancellationReason?: string;
}

export interface RxSafetyResult {
  medication: string;
  normalized: string;
  allergyHits: Array<{
    allergen: string;
    source: 'patient' | 'history';
    match?: 'direct' | 'class' | 'cross-reactive';
    note?: string;
  }>;
  interactionHits: Array<{
    withMedication: string;
    severity: string;
    effect: string;
    management: string;
  }>;
  activeMedsChecked: number;
  /** Curated critical list — an empty result is NOT a comprehensive clean check. */
  coverage: 'critical-list';
}

export const prescriptionApi = {
  // Create a new prescription
  create: async (data: CreatePrescriptionDto): Promise<Prescription> => {
    const response = await apiClient.post('/prescriptions', data);
    return response.data.data;
  },

  // Advisory pre-prescription safety check: allergies + active meds vs
  // the platform's critical drug-interaction pairs.
  checkInteractions: async (
    patientId: string,
    medicationName: string,
  ): Promise<RxSafetyResult> => {
    const response = await apiClient.post('/prescriptions/interaction-check', {
      patientId,
      medicationName,
    });
    return response.data.data ?? response.data;
  },

  // Create several medications written together as ONE session group. The
  // backend generates a shared prescriptionGroupId and stamps every item,
  // so the script surfaces as a single row in the grouped lists. Returns
  // the resulting group.
  createBatch: async (
    items: CreatePrescriptionDto[],
  ): Promise<PrescriptionGroup> => {
    const response = await apiClient.post('/prescriptions/batch', { items });
    return response.data.data;
  },

  // Get all prescriptions with optional filters (flat array — drops pagination
  // metadata; used by callers that want a plain list).
  getAll: async (filters?: PrescriptionFilterDto): Promise<Prescription[]> => {
    const response = await apiClient.get('/prescriptions', { params: filters });
    const result = response.data.data;
    return Array.isArray(result) ? result : result?.data ?? [];
  },

  // Paginated list — preserves total/page/limit for server-side paging.
  list: async (
    filters?: PrescriptionFilterDto,
  ): Promise<PaginatedPrescriptions> => {
    const response = await apiClient.get('/prescriptions', { params: filters });
    const result = response.data.data;
    // Backend returns { data, total, page, limit }; older shapes may return a
    // bare array — normalize either way.
    if (Array.isArray(result)) {
      return { data: result, total: result.length, page: 1, limit: result.length };
    }
    return {
      data: result?.data ?? [],
      total: result?.total ?? 0,
      page: result?.page ?? 1,
      limit: result?.limit ?? 20,
    };
  },

  // Session-grouped list — one entry per encounter (a full consult's
  // prescription as a single unit) instead of one row per medication.
  listGroups: async (
    filters?: PrescriptionFilterDto,
  ): Promise<PaginatedPrescriptionGroups> => {
    const response = await apiClient.get('/prescriptions/groups', {
      params: filters,
    });
    const result = response.data.data;
    if (Array.isArray(result)) {
      return { data: result, total: result.length, page: 1, limit: result.length };
    }
    return {
      data: result?.data ?? [],
      total: result?.total ?? 0,
      page: result?.page ?? 1,
      limit: result?.limit ?? 20,
    };
  },

  // All medications prescribed together in one session. `groupId` is an
  // encounter id (medicalRecordId) or a standalone prescription's id.
  getGroup: async (groupId: string): Promise<PrescriptionGroup> => {
    const response = await apiClient.get(`/prescriptions/groups/${groupId}`);
    return response.data.data;
  },

  // Attach medications to an existing session group. The returned group's
  // `groupKey` may differ from `groupId` when a singleton is promoted to a
  // batch — callers should re-route to the returned key.
  addToGroup: async (
    groupId: string,
    items: CreatePrescriptionDto[],
  ): Promise<PrescriptionGroup> => {
    const response = await apiClient.post(
      `/prescriptions/groups/${groupId}/items`,
      { items },
    );
    return response.data.data;
  },

  // Clinic-wide stat-card counts (total + active across all pages).
  getStats: async (): Promise<{ total: number; active: number }> => {
    const response = await apiClient.get('/prescriptions/stats');
    return response.data.data;
  },

  // Get a single prescription by ID
  getById: async (id: string): Promise<Prescription> => {
    const response = await apiClient.get(`/prescriptions/${id}`);
    return response.data.data;
  },

  // Get prescriptions for a specific patient
  // `/prescriptions` is paginated and defaults to 20 a page server-side, so
  // omitting `limit` silently capped a patient's medication list at the 20
  // most recent scripts — and every count derived from it (the chart's
  // "Medicines" chip, the header's meds tile, the "Taking now" panel) read
  // as complete. A chronic patient on repeats crosses 20 inside two years.
  // `CHART_RX_PAGE` is the ceiling the chart asks for in one go.
  getByPatient: async (
    patientId: string,
    limit: number = CHART_RX_PAGE,
  ): Promise<Prescription[]> => {
    const response = await apiClient.get('/prescriptions', {
      params: { patientId, limit },
    });
    const result = response.data.data;
    return Array.isArray(result) ? result : result?.data ?? [];
  },

  // Update a prescription
  update: async (id: string, data: UpdatePrescriptionDto): Promise<Prescription> => {
    const response = await apiClient.patch(`/prescriptions/${id}`, data);
    return response.data.data;
  },

  // Delete a prescription
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/prescriptions/${id}`);
  },

  // Sign a prescription. signatureHash + ipAddress are derived server-side,
  // so there's nothing to send — the backend captures the IP from the request
  // and generates the integrity hash itself.
  sign: async (id: string): Promise<Prescription> => {
    const response = await apiClient.post(`/prescriptions/${id}/sign`, {});
    return response.data.data;
  },

  // Transmit prescription to pharmacy
  transmit: async (id: string, transmissionData?: { transmissionId?: string }): Promise<Prescription> => {
    const response = await apiClient.post(`/prescriptions/${id}/transmit`, transmissionData);
    return response.data.data;
  },

  // Cancel a prescription
  cancel: async (id: string, reason: string): Promise<Prescription> => {
    const response = await apiClient.post(`/prescriptions/${id}/cancel`, { reason });
    return response.data.data;
  },

  // Update refills remaining
  updateRefills: async (id: string, refillsRemaining: number): Promise<Prescription> => {
    const response = await apiClient.patch(`/prescriptions/${id}/refills`, { refillsRemaining });
    return response.data.data;
  }
};
